import {
  createContext,
  createRef,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';

export const menuToggleContext = createContext<{
  state: boolean;
  toggle: (state?: boolean) => void;
  hamburgerRef?: React.RefObject<HTMLButtonElement>;
  menuRef?: React.RefObject<HTMLDivElement>;
} | null>(null);

export function useMenuToggle(optional?: boolean) {
  const menuCtx = useContext(menuToggleContext);

  if (optional && !menuCtx) {
    return {
      state: false,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      toggle: () => {},
      hamburgerRef: createRef<HTMLButtonElement>(),
      menuRef: createRef<HTMLDivElement>(),
    };
  }

  if (!menuCtx) {
    throw new Error('useMenuToggle must be used within a MenuProvider');
  }

  const { state, toggle, hamburgerRef, menuRef } = menuCtx;

  useEffect(() => {
    const isMobile = window.matchMedia('(width < 835px)');
    const hamburgerButton = hamburgerRef?.current;
    const wrapper = menuRef?.current;
    const mainMenu = wrapper?.querySelector('nav');
    const closeOnClick: EventListener = (event) => {
      if (
        isMobile.matches && state && mainMenu?.contains(event.target as Node)
          ? (event.target as HTMLElement).closest('a')
          : !hamburgerButton?.contains(event.target as Node)
      ) {
        toggle(false);
      }
    };

    window.addEventListener('click', closeOnClick);

    return () => {
      window.removeEventListener('click', closeOnClick);
    };
  }, [state, hamburgerRef, menuRef, toggle]);

  return menuCtx;
}

export function MenuProvider({ children }: PropsWithChildren) {
  const { Provider } = menuToggleContext;
  const [menuOpen, setMenuOpen] = useState(false);
  const hamburgerRef = createRef<HTMLButtonElement>();
  const menuRef = createRef<HTMLDivElement>();

  return (
    <Provider
      value={{
        hamburgerRef,
        menuRef,
        state: menuOpen,
        toggle: (state?: boolean) =>
          typeof state !== 'undefined'
            ? setMenuOpen(state)
            : setMenuOpen((prev) => !prev),
      }}
    >
      {children}
    </Provider>
  );
}
